import { Injectable } from '@angular/core';
import { ApiHttpService } from './api-http.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { Constants } from '../../../config/constants';
import { BaseURLParams } from 'src/app/shared/interfaces/base-params';
import jwt_decode from 'jwt-decode';
import { Customer } from './things-board/api.tb.customer.service';

@Injectable({
  providedIn: 'root'
})
export class ApiAuthService {

  private _cookieConfig: {
    path?: string,
    domain?: string,
    secure?: boolean,
    sameSite?: 'Lax' | 'None' | 'Strict'
  } = {
      path: '/',
      domain: location.hostname,
      secure: location.protocol === 'https:',
      sameSite: 'Lax',
    };
  constructor(
    protected _apiHttpService: ApiHttpService,
    protected _cookieService: CookieService,
  ) { }

  private _authHandler(response: IAuthResponse, refresh: boolean = false): boolean {
    if (!refresh) {
      this._deleteCookie('customerId');
    }

    this._setCookie('token', response.token);
    this._setCookie('refreshToken', response.refreshToken);

    // if the user that just logged in is a sys_admin, we store the creds in a different cookie
    // so it can be retrieved when the admin switches between users
    const user: User = jwt_decode(response.token);
    if (user.scopes.indexOf(UserScopes.SYS_ADMIN) > -1) {
      this._setCookie('admin_token', response.token);
      this._setCookie('admin_refreshToken', response.refreshToken);
    } else if (user.scopes.indexOf(UserScopes.CUSTOMER_USER) > -1) {
      this._setCookie('customerId', user.customerId);
      this.getCustomer().subscribe();
    } else if (!refresh && response.customerId && response.customerId !== 'undefined' && response.customerId !== '') {
      this._setCookie('customerId', response.customerId);
      this.getCustomer().subscribe();
    }
    return true;
  }

  authenticate(username: string, password: string, rememberMe: boolean): Observable<boolean> {
    const params: BaseURLParams = {
      url: Constants.API_THINGSBOARD_URL + '/auth/login',
      body: {
        username,
        password
      }
    };
    return this._apiHttpService.post(params)
      .pipe(
        map((data: IAuthResponse) => this._authHandler(data)),
        catchError(this._apiHttpService.handleError)
      );
  }

  refreshToken(): Observable<boolean> {
    const params: BaseURLParams = {
      url: Constants.API_THINGSBOARD_URL + '/auth/token',
      body: {
        refreshToken: this._getUserLoginCookies().refreshToken,
      }
    };
    return this._apiHttpService.post(params)
      .pipe(
        map((data: IAuthResponse) => this._authHandler(data, true)),
        catchError(this._apiHttpService.handleError)
      );
  }

  logout(): Observable<boolean> {
    this._deleteAdminLoginCookies();
    this._deleteUserLoginCookies();
    return of(true);
  }

  switchCustomer(customer: Customer): Observable<boolean> {
    if (customer){
      this._setCookie('customerId', customer?.id?.id);
      sessionStorage.setItem('customer', JSON.stringify(customer));
      return of(true);
    } else {
      this._deleteCookie('customerId');
      sessionStorage.removeItem('customer');
      return of(false);
    }
  }

  switchToUser(userTokenData: IAuthResponse): Observable<boolean> {
    this._setAdminLoginCookies(this._getUserLoginCookies());
    this._setUserLoginCookies(userTokenData);
    return of(true);
  }

  switchToAdmin(): Observable<boolean> {
    this._deleteUserLoginCookies();
    this._setUserLoginCookies(this._getAdminLoginCookies());
    return of(true);
  }

  getUserToken(): string {
    return this._getUserLoginCookies().token;
  }

  getAdminToken(): string {
    return this._getAdminLoginCookies().token;
  }

  getCustomerId(): string {
    return this._getAdminLoginCookies().customerId;
  }

  private _setCookie(name: string, value: any, expires: number = 0): void {
    this._cookieService.set(
      name,
      value,
      expires,
      this._cookieConfig.path,
      this._cookieConfig.domain,
      this._cookieConfig.secure,
      this._cookieConfig.sameSite
    );
  }

  private _deleteCookie(name: string): void {
    this._cookieService.delete(
      name,
      this._cookieConfig.path,
      this._cookieConfig.domain,
      this._cookieConfig.secure,
      this._cookieConfig.sameSite
    );
  }

  private _getUserLoginCookies(): IAuthResponse {
    let token = this._cookieService.get('token');
    token = token != null && token !== '' ? token : null;
    let refreshToken = this._cookieService.get('refreshToken');
    refreshToken = refreshToken != null && refreshToken !== '' ? refreshToken : null;
    let customerId = this._cookieService.get('customerId');
    customerId = customerId && customerId !== '' && customerId !== 'undefined' ? customerId : null;
    return {
      customerId,
      token,
      refreshToken
    };
  }

  private _setUserLoginCookies(tokenData: IAuthResponse): void {
    this._setCookie('token', tokenData.token);
    this._setCookie('refreshToken', tokenData.refreshToken);

    // Sets customer to cache
    if (tokenData.customerId) {
      this._setCookie('customerId', tokenData.customerId);
      this.getCustomer().subscribe();
    }
  }

  private _deleteUserLoginCookies(): void {
    this._deleteCookie('token');
    this._deleteCookie('refreshToken');

    // Removes customer from cache
    this._deleteCookie('customerId');
    sessionStorage.removeItem('customer');
    sessionStorage.removeItem('tenant');
  }

  private _getAdminLoginCookies(): IAuthResponse {
    let token = this._cookieService.get('admin_token');
    token = token != null && token !== '' ? token : null;
    let refreshToken = this._cookieService.get('admin_refreshToken');
    refreshToken = refreshToken != null && refreshToken !== '' ? refreshToken : null;
    let customerId = this._cookieService.get('customerId');
    customerId = customerId != null && customerId !== '' ? customerId : null;
    return {
      customerId,
      token,
      refreshToken
    };
  }

  private _setAdminLoginCookies(tokenData: IAuthResponse): void {
    this._setCookie('admin_token', tokenData.token);
    this._setCookie('admin_refreshToken', tokenData.refreshToken);

    // Removes Customer from cache
    this._deleteCookie('customerId');
    sessionStorage.removeItem('customer');
  }

  private _deleteAdminLoginCookies(): void {
    this._deleteCookie('admin_token');
    this._deleteCookie('admin_refreshToken');

    // Removes Customer from cache
    this._deleteCookie('customerId');
  }

  getCustomer(): Observable<any> {
    const customerId = this.getCustomerId();
    const params: BaseURLParams = {
      url: Constants.API_VSBLTY_URL + `/customer/${customerId}`,
      options: {
        headers: {
          'X-Authorization': 'Bearer ' + this._getUserLoginCookies().token
        }
      }
    };

    const saveCustomerSession = (result) => {
      result.id = {
        entityType: 'CUSTOMER',
        id: result?.id?.toString()
      };

      result.tenantId = {
        entityType: 'TENANT',
        id: result?.tenantId?.toString()
      };

      result.tenant.id = {
        entityType: 'TENANT',
        id: result?.tenantId?.toString()
      };

      console.log('Session', result);

      sessionStorage.setItem('customer', JSON.stringify(result));
      return result;
    };

    return this._apiHttpService.get(params).pipe(
      map(result => saveCustomerSession(result.Entity)),
      catchError(this._apiHttpService.handleError)
    );
  }


}

export interface IAuthResponse {
  customerId?: string;
  token: string;
  refreshToken: string;
}

export enum UserScopes {
  SYS_ADMIN = 'SYS_ADMIN',
  TENANT_ADMIN = 'TENANT_ADMIN',
  CUSTOMER_USER = 'CUSTOMER_USER',
  REFRESH_TOKEN = 'REFRESH_TOKEN',
}

export interface User {
  sub: string; // email address;
  scopes: Array<UserScopes>;
  userId: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  isPublic: boolean;
  tenantId: string;
  customerId: string;
  iss: string; // url;
  iat: number;
  exp: number;
  thumbUrl?: string; // used locally
}


/**
 * Check if the current user token is valid (not expired)
 * @returns true if token exists and is not expired, false otherwise
 */
export function isTokenValid(token: string): boolean {
  try {
    const user = jwt_decode<any>(token);
    if (!user || !user.exp) {
      return false;
    }

    // JWT exp is in seconds, Date.now() is in milliseconds
    const currentTimestamp = Math.floor(Date.now() / 1000);
    return user.exp > currentTimestamp;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
}

/**
 * Get the expiration date of the current token
 * @returns Date object representing token expiration, or null if token invalid
 */
export function getTokenExpirationDate(token: string): Date | null {
  try {
    const user = jwt_decode<any>(token);
    if (!user || !user.exp) {
      return null;
    }

    // JWT exp is in seconds, convert to milliseconds
    return new Date(user.exp * 1000);
  } catch (error) {
    console.error('Error getting token expiration:', error);
    return null;
  }
}
